"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, Calendar, Compass, MapPin, Sparkles } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
const relatedItems = [{
  title: "Local SEO",
  path: "/LocalSEOPage",
  tagline: "High-intent local visibility",
  description: "Target high-intent searchers across your regional market with tailored Google Business Profile optimisation and local citation architecture.",
  icon: MapPin
}, {
  title: "UK Cities & Areas",
  path: "/UkCitiesAreasPage",
  tagline: "Location-focused campaigns",
  description: "Explore regional SEO coverage across key UK metropolitan hubs, tailored to regional search patterns and local buyer intent.",
  icon: Compass
}];
export default function RelatedServices() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="relatedservicescta" data-nav="light" className="bg-background py-20 md:py-28 relative overflow-hidden">
      {/* Subtle decorative background tint */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 right-0 h-96 w-96 rounded-full bg-secondary/60 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 left-0 h-80 w-80 rounded-full bg-secondary/40 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-6xl">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="space-y-16">
          {/* Section Header */}
          <div className="max-w-2xl mx-auto text-center space-y-3">
            <Badge variant="secondary" className="px-3 py-1 font-medium text-foreground gap-1.5">
              <Sparkles className="size-3.5 text-accent" />
              Related search specialisms
            </Badge>
            <h2 className="font-default text-3xl sm:text-4xl font-semibold tracking-tight text-foreground">
              Explore related location services
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg">
              Compare complementary search specialisms below, or get in touch to discuss which location service best fits your target market.
            </p>
          </div>

          {/* Related Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {relatedItems.map((item, i) => {
            const Icon = item.icon;
            return <div data-index={i} key={item.path} className="h-full">
                  <motion.div variants={itemVariants} className="h-full">
                    <Card className="h-full border border-border bg-card shadow-xs hover:border-primary/40 hover:shadow-md transition-[border-color,box-shadow] duration-300">
                      <CardContent className="p-8 flex flex-col justify-between h-full gap-6">
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div className="size-12 rounded-lg bg-secondary flex items-center justify-center text-primary">
                              <Icon className="size-6" />
                            </div>
                            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                              {item.tagline}
                            </span>
                          </div>

                          <div className="space-y-2">
                            <h3 className="font-default text-2xl font-semibold text-foreground">
                              {item.title}
                            </h3>
                            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                              {item.description}
                            </p>
                          </div>
                        </div>

                        <div className="pt-2">
                          <Button asChild variant="outline" className="w-full justify-between group hover:bg-primary hover:text-primary-foreground transition-colors">
                            <Link to={item.path}>
                              <span>View {item.title}</span>
                              <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                            </Link>
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                </div>;
          })}
          </div>

          {/* Bottom Consultation CTA Banner */}
          <motion.div variants={itemVariants} className="rounded-2xl border border-border bg-card p-8 sm:p-12 text-center relative overflow-hidden shadow-xs">
            <div className="max-w-2xl mx-auto space-y-6">
              <div className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                  Direct Expert Consultation
                </span>
                <h3 className="font-default text-2xl sm:text-3xl font-semibold text-foreground">
                  Ready to grow your search visibility?
                </h3>
                <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                  Discuss how these related services can work together for your business. Book a free consultation directly with me to identify the right local or regional strategy.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
                <Button asChild size="lg" className="w-full sm:w-auto font-medium px-8 shadow-sm">
                  <Link to="/ContactPage">
                    <SafeHydrate><Calendar className="size-4" /></SafeHydrate>
                    <span>Book a Free SEO Consultation</span>
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}